import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle, CheckCircle, Loader2, User, Shield, Bell, Send, Server, Play, Square, RotateCw, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getAuthenticatedAxios, getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const UserSettings = () => {
  const { user } = useAuth();
  const [restarting, setRestarting] = useState(false);
  const [restartStatus, setRestartStatus] = useState(null);
  
  // Telegram notification settings
  const [telegramSettings, setTelegramSettings] = useState({
    bot_token: '8423948061:AAGdVFvPdwJpm18qI8wUqQTIKveLRTaWSDk',
    chat_id: '',
    threshold_buy_usd: 0.0,
    threshold_sell_usd: 0.0,
    enabled: false
  });
  const [loadingTelegram, setLoadingTelegram] = useState(false);
  const [savingTelegram, setSavingTelegram] = useState(false);
  const [testingTelegram, setTestingTelegram] = useState(false);
  
  // Server management
  const [serverStatus, setServerStatus] = useState(null);
  const [loadingServerStatus, setLoadingServerStatus] = useState(false);
  const [serverAction, setServerAction] = useState(null);

  const handleRestartService = async () => {
    if (!confirm('Вы уверены, что хотите перезапустить Market Maker сервис? Это остановит все текущие операции и перезагрузит все сервисы.')) {
      return;
    }

    setRestarting(true);
    setRestartStatus(null);

    try {
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/system/restart-service`);
      
      if (response.data.success) {
        setRestartStatus('success');
        toast.success('Сервис успешно перезапущен');
        
        // Через 3 секунды обновим страницу для проверки статуса
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        setRestartStatus('error');
        toast.error(response.data.message || 'Ошибка при перезапуске сервиса');
      }
    } catch (error) {
      console.error('Error restarting service:', error);
      setRestartStatus('error');
      
      // Проверяем тип ошибки
      if (error.response?.status === 401) {
        toast.error('Ошибка авторизации. Пожалуйста, выйдите и войдите снова.');
      } else if (error.response?.status === 403) {
        toast.error('Недостаточно прав. Требуются права администратора с полным доступом.');
      } else if (error.response?.status === 404) {
        toast.error('Эндпоинт не найден. Убедитесь, что бэкенд перезапущен.');
      } else {
        toast.error(error.response?.data?.detail || error.message || 'Ошибка при перезапуске сервиса');
      }
    } finally {
      setRestarting(false);
    }
  };

  // Load Telegram settings and server status on mount
  useEffect(() => {
    loadTelegramSettings();
    loadServerStatus();
  }, []);

  const loadTelegramSettings = async () => {
    try {
      setLoadingTelegram(true);
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/user-settings/telegram`);
      
      if (response.data.success && response.data.settings) {
        setTelegramSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error loading Telegram settings:', error);
    } finally {
      setLoadingTelegram(false);
    }
  };

  const saveTelegramSettings = async () => {
    try {
      setSavingTelegram(true);
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/user-settings/telegram`, telegramSettings);
      
      if (response.data.success) {
        toast.success('Настройки Telegram уведомлений сохранены');
      } else {
        toast.error('Ошибка при сохранении настроек');
      }
    } catch (error) {
      console.error('Error saving Telegram settings:', error);
      toast.error(error.response?.data?.detail || 'Ошибка при сохранении настроек');
    } finally {
      setSavingTelegram(false);
    }
  };

  const testTelegramNotification = async () => {
    try {
      setTestingTelegram(true);
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/user-settings/telegram/test`);
      
      if (response.data.success) {
        toast.success('Тестовое уведомление отправлено');
      } else {
        toast.error('Ошибка при отправке тестового уведомления');
      }
    } catch (error) {
      console.error('Error testing Telegram notification:', error);
      toast.error(error.response?.data?.detail || 'Ошибка при отправке тестового уведомления');
    } finally {
      setTestingTelegram(false);
    }
  };

  const loadServerStatus = async () => {
    try {
      setLoadingServerStatus(true);
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/user-settings/server/status`);
      if (response.data.success) {
        setServerStatus(response.data.status);
      }
    } catch (error) {
      console.error('Error loading server status:', error);
      toast.error(error.response?.data?.detail || 'Ошибка при загрузке статуса сервера');
    } finally {
      setLoadingServerStatus(false);
    }
  };

  const manageServer = async (action) => {
    if (!confirm(`Вы уверены, что хотите ${action === 'start' ? 'запустить' : action === 'stop' ? 'остановить' : 'перезапустить'} сервер?`)) {
      return;
    }

    setServerAction(action);
    try {
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/user-settings/server/${action}`);
      
      if (response.data.success) {
        toast.success(response.data.message || `Сервер ${action === 'start' ? 'запущен' : action === 'stop' ? 'остановлен' : 'перезапущен'} успешно`);
        setTimeout(() => {
          loadServerStatus();
          if (action === 'restart') {
            setTimeout(() => window.location.reload(), 2000);
          }
        }, 2000);
      }
    } catch (error) {
      console.error(`Error ${action}ing server:`, error);
      toast.error(error.response?.data?.detail || error.message || `Ошибка при ${action === 'start' ? 'запуске' : action === 'stop' ? 'остановке' : 'перезапуске'} сервера`);
    } finally {
      setServerAction(null);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Настройки пользователя</h1>
          <p className="text-gray-500 mt-1">Управление настройками аккаунта и системой</p>
        </div>
      </div>

      {/* User Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Информация о пользователе
          </CardTitle>
          <CardDescription>Текущие данные вашего аккаунта</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">Имя пользователя</p>
                <p className="text-lg font-semibold">{user?.username || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">Роль</p>
                <p className="text-lg font-semibold capitalize">{user?.role || 'Viewer'}</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">Доступ</p>
                <div className="flex items-center gap-2 mt-1">
                  {user?.full_access ? (
                    <>
                      <Shield className="h-4 w-4 text-green-600" />
                      <p className="text-lg font-semibold text-green-600">Full Access</p>
                    </>
                  ) : (
                    <p className="text-lg font-semibold text-gray-600">Limited Access</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Management Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Управление системой
          </CardTitle>
          <CardDescription>Перезапуск Market Maker сервиса и всех используемых компонентов</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-yellow-900 mb-1">Внимание!</h4>
                  <p className="text-sm text-yellow-800">
                    Перезапуск сервиса остановит все текущие операции Market Maker, 
                    перезагрузит все используемые сервисы и скрипты. Это действие может занять 
                    несколько секунд. Убедитесь, что вы готовы к временной остановке работы системы.
                  </p>
                </div>
              </div>
            </div>

            {restartStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1">Сервис перезапущен</h4>
                    <p className="text-sm text-green-800">
                      Market Maker сервис успешно перезапущен. Страница будет обновлена через несколько секунд.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {restartStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <div>
                    <h4 className="font-semibold text-red-900 mb-1">Ошибка перезапуска</h4>
                    <p className="text-sm text-red-800">
                      Не удалось перезапустить сервис. Проверьте логи или попробуйте снова.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Перезапуск Market Maker сервиса</h4>
                <p className="text-sm text-gray-600">
                  Полностью перезагрузит Market Maker сервис и все используемые компоненты системы
                </p>
              </div>
              <Button
                onClick={handleRestartService}
                disabled={restarting || restartStatus === 'success'}
                variant="destructive"
                className="ml-4"
              >
                {restarting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Перезапуск...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Перезапустить сервис
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Telegram Notifications Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Telegram Уведомления
          </CardTitle>
          <CardDescription>
            Настройка уведомлений о покупках и продажах выше указанной суммы
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingTelegram ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Bell className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 mb-1">Информация</h4>
                    <p className="text-sm text-blue-800">
                      Уведомления будут отправляться в Telegram бот при покупке или продаже выше указанной суммы в USD.
                      Для получения Chat ID отправьте команду /start боту, затем используйте его ID.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Токен бота</label>
                  <Input
                    type="text"
                    value={telegramSettings.bot_token}
                    onChange={(e) => setTelegramSettings({...telegramSettings, bot_token: e.target.value})}
                    placeholder="8423948061:AAGdVFvPdwJpm18qI8wUqQTIKveLRTaWSDk"
                    className="font-mono text-xs"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Chat ID</label>
                  <Input
                    type="text"
                    value={telegramSettings.chat_id}
                    onChange={(e) => setTelegramSettings({...telegramSettings, chat_id: e.target.value})}
                    placeholder="123456789"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Для получения Chat ID:
                    <br />1. Найдите бота @solanabeast_bot в Telegram
                    <br />2. Отправьте команду /start
                    <br />3. Используйте ваш Telegram ID (можно получить через @userinfobot)
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Порог для покупки (USD)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={telegramSettings.threshold_buy_usd}
                      onChange={(e) => setTelegramSettings({...telegramSettings, threshold_buy_usd: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Минимальная сумма покупки для уведомления
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Порог для продажи (USD)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={telegramSettings.threshold_sell_usd}
                      onChange={(e) => setTelegramSettings({...telegramSettings, threshold_sell_usd: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Минимальная сумма продажи для уведомления
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="telegram_enabled"
                    checked={telegramSettings.enabled}
                    onChange={(e) => setTelegramSettings({...telegramSettings, enabled: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label htmlFor="telegram_enabled" className="text-sm font-medium">
                    Включить уведомления
                  </label>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <Button
                    onClick={saveTelegramSettings}
                    disabled={savingTelegram}
                    className="flex-1"
                  >
                    {savingTelegram ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Сохранение...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Сохранить настройки
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={testTelegramNotification}
                    disabled={testingTelegram || !telegramSettings.enabled || !telegramSettings.chat_id}
                    variant="outline"
                  >
                    {testingTelegram ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Отправка...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Тест
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Server Management Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Управление сервером
          </CardTitle>
          <CardDescription>Управление бэкенд сервером и его статусом</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Server Status */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                {loadingServerStatus ? (
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                ) : serverStatus?.running ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                )}
                <div>
                  <div className="font-medium">
                    Статус: {loadingServerStatus ? 'Проверка...' : serverStatus?.running ? 'Работает' : 'Остановлен'}
                  </div>
                  <div className="text-sm text-gray-500">
                    Порт 8000: {serverStatus?.port_8000 === 'occupied' ? 'Занят' : 'Свободен'} | 
                    Health: {serverStatus?.health_check ? 'OK' : 'FAIL'}
                    {serverStatus?.systemd_active && ' | Systemd: Active'}
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadServerStatus}
                disabled={loadingServerStatus}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingServerStatus ? 'animate-spin' : ''}`} />
                Обновить
              </Button>
            </div>

            {/* Control Buttons */}
            <div className="grid grid-cols-3 gap-3">
              <Button
                onClick={() => manageServer('start')}
                disabled={serverAction !== null || serverStatus?.running}
                variant="outline"
                className="flex items-center gap-2"
              >
                {serverAction === 'start' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                Запустить
              </Button>
              
              <Button
                onClick={() => manageServer('stop')}
                disabled={serverAction !== null || !serverStatus?.running}
                variant="outline"
                className="flex items-center gap-2"
              >
                {serverAction === 'stop' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
                Остановить
              </Button>
              
              <Button
                onClick={() => manageServer('restart')}
                disabled={serverAction !== null}
                variant="outline"
                className="flex items-center gap-2"
              >
                {serverAction === 'restart' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RotateCw className="h-4 w-4" />
                )}
                Перезапустить
              </Button>
            </div>

            {/* Instructions */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="space-y-2 text-sm">
                  <div className="font-medium text-blue-900">Инструкция по управлению сервером:</div>
                  <ul className="list-disc list-inside space-y-1 text-blue-800">
                    <li><strong>Запустить</strong> - запускает сервер, освобождает порт 8000 и проверяет работоспособность</li>
                    <li><strong>Остановить</strong> - останавливает все процессы сервера и освобождает порт</li>
                    <li><strong>Перезапустить</strong> - полностью перезапускает сервер (рекомендуется при проблемах с авторизацией)</li>
                  </ul>
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <div className="font-medium text-blue-900">Команды для терминала:</div>
                    <div className="font-mono text-xs bg-blue-100 p-2 rounded mt-1 space-y-1">
                      <div>systemctl start tonmm-backend    # Запустить</div>
                      <div>systemctl stop tonmm-backend     # Остановить</div>
                      <div>systemctl restart tonmm-backend  # Перезапустить</div>
                      <div>systemctl status tonmm-backend   # Статус</div>
                      <div>cd /root/tonmm/TONMM/backend && bash restart_server.sh  # Ручной перезапуск</div>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-blue-200 text-xs text-blue-700">
                    <strong>Примечание:</strong> Сервер автоматически перезапускается при сбоях (через systemd) и проверяется каждые 5 минут (через cron).
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserSettings;

